import { P, match } from 'ts-pattern';
import { $EndPanel } from '../__generated/enhanced-model.js';
import { implementCustomExpressions } from '../__generated/implementation.js';
import { BurnerBaseW24Variant, EvoAffinity25G, EvoAffinity30G, FullDoorBase, GrillBaseVariant, Handing, LynxAsadoGriddgle, LynxKamadoGrill, LynxProfessionalCocktailStation, LynxProfessionalDoubleBurner, LynxProfessionalGrillW30, LynxProfessionalGrillW36, LynxProfessionalGrillW42, LynxProfessionalGrillW54, LynxProfessionalPowerBurner, LynxProfessionalSingleBurner, LynxSedonaDoubleBurner, LynxSedonaGrillW30, LynxSedonaGrillW36, LynxSedonaGrillW42, LynxSedonaSingleBurner, LynxSink, LynxSinkW18, LynxSinkW24, LynxSinkW30, SinkBase } from '../__generated/model/index.js';
import { MaterialsMap, StainlessMaterialProperties, ToeKickObjectName, cabinetData, defaultCountertopData, getCountertopData, getToeKickData } from '../data/cabinets.js';
import { StackExpressions } from '../other/stack-expressions.js';
import { round } from '../util/round.js';
import { CabinetWidth, NO_HANDING, adjustableDoorHandingOptions } from './common.js';
const getCabinetData = (s)=>{
    const data = cabinetData[s.$dataKey];
    if (!data) {
        throw new Error(`No cabinet data found for type: ${s.type} (${s.$dataKey})`);
    }
    return data;
};
const getAppliancePanelMaterialProperties = (enabled, color)=>{
    if (enabled) {
        return MaterialsMap[color] ?? StainlessMaterialProperties;
    }
    return StainlessMaterialProperties;
};
export const cabinetExpressions = implementCustomExpressions({
    AdjustableDoorCabinet: {
        // IMPLEMENT THIS IN CHILD ENTITY EXPRESSIONS FOR DYNAMIC DOOR HEIGHT
        // $doorHeight: s => s.$netHeight,
        // $doorOffsetY: () => 35.3 * constants.Room.$inchToMm,
        $isOneWingDoorCabinet: (s)=>s.handing === Handing.left || s.handing === Handing.right,
        $handingOptions: ()=>adjustableDoorHandingOptions,
        $doorWidth: (s)=>s.$isOneWingDoorCabinet || s.handing === 'top' || s.handing === 'none' ? s.$netWidth : s.$netWidth / 2,
        $doorLeftFilter: (s)=>s.handing === Handing.both || s.handing === Handing.left,
        $doorRightFilter: (s)=>s.handing === Handing.both || s.handing === Handing.right,
        $doorTopFilter: (s)=>s.handing === Handing.top,
        $handleLeftFilter: (s)=>s.$doorLeftFilter,
        $handleRightFilter: (s)=>s.$doorRightFilter,
        $handleTopFilter: (s)=>s.$doorTopFilter && s.handing !== Handing.none,
        $doorLeftOffsetX: ()=>0,
        $doorRightOffsetX: (s)=>!s.$isOneWingDoorCabinet || s.handing === 'none' ? s.$netWidth / 2 : 0,
        $doorOffsetZ: ()=>5,
        $handleLeftOffsetX: (s)=>s.$doorWidth - s.$handleSideOffset,
        $handleRightOffsetX: (s)=>s.$doorRightOffsetX + s.$handleSideOffset,
        $handleTopOffsetX: (s)=>s.$netWidth / 2,
        $handleTopOffsetY: (s)=>s.$doorOffsetY - s.$handleSideOffset,
        $handleOffsetZ: (s)=>s.$handleDepthOffset,
        $handleOffsetY: (s)=>s.$doorHeight - s.$handleLength / 2 - s.$handleSideOffset * 3 + s.$room.$toeKickHeight,
        $handleLength: (s)=>{
            if (s.$isOneWingDoorCabinet && s.$doorHeight > s.$room.$minCabinetHeight * 0.7) {
                return s.$room.$largeHandleLength;
            }
            return s.$room.$smallHandleLength;
        }
    },
    BurnerBaseW18: {
        $doorHeight: (s)=>s.$frontPanelHeight * 0.664,
        $doorOffsetY: (s)=>s.$frontPanelHeight * 0.664 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang
    },
    BurnerBaseW24: {
        $dataKey: (s)=>`${s.type}_${round(s.$netWidth, 2)}_${s.variant}_${s.handing}`,
        $bodyObjectName: (s)=>{
            return match(s.variant).with(BurnerBaseW24Variant.CounterTopHeight, ()=>'CABINET_LYNX_BBFD_24_BODY').with(BurnerBaseW24Variant.Lowered, ()=>'CABINET_LYNX-BBFD-24').exhaustive();
        },
        $doorHeight: (s)=>s.variant === BurnerBaseW24Variant.Lowered ? s.$frontPanelHeight * 0.375 : s.$frontPanelHeight * 0.664,
        $doorOffsetY: (s)=>s.variant === BurnerBaseW24Variant.Lowered ? s.$frontPanelHeight * 0.375 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang : s.$frontPanelHeight * 0.664 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang
    },
    // LoweredBurnerBaseW24: {
    //   $doorHeight: s => s.$frontPanelHeight * 0.375,
    //   $doorOffsetY: s => s.$frontPanelHeight * 0.375 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
    // },
    BurnerBase: {
        $countertopSideOuterWidth: ()=>CabinetWidth.W3,
        $countertopLeftSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: ()=>0
    },
    BottomCabinet: {
        $width: (s)=>round(s.$netWidth + s.$room.$cabinetSideGap, 4),
        $priceSku: (s)=>s.$sku,
        $name: (s)=>getCabinetData(s).name,
        $sku: (s)=>getCabinetData(s).sku,
        $description: (s)=>getCabinetData(s).description,
        $thumbnailUrl: (s)=>getCabinetData(s).thumbnailUrl ?? ''
    },
    BottomCabinetWithDefaultDetail: {
        $dataKey: (s)=>{
            const handing = 'handing' in s ? s.handing : NO_HANDING;
            return `${s.type}_${round(s.$netWidth, 2)}_${handing}`;
        }
    },
    BottomWallItem: {
        $offsetZ: (s)=>s.$wall.$maxWallItemDepth,
        $selectHotspotOffsetX: (s)=>s.$width / 2,
        $selectHotspotOffsetY: (s)=>s.$height / 2,
        $selectHotspotOffsetZ: ()=>10,
        $backPanelFilter: (s)=>s.$wall.$active && !s.$wall.fullHeight,
        $countertopDepth: (s)=>{
            const baseDepth = s.$wall.$maxWallItemDepth + s.$countertopOffsetZ;
            if (s.$backPanelFilter) {
                return baseDepth + s.$room.$backPanelDepth;
            }
            return baseDepth;
        },
        $countertopExtraWidth: (s)=>{
            return {
                left: s.$leftSideOpen ? s.$endPanelWidth + s.$countertopSideOverhang : s.$room.$cabinetSideGap,
                right: s.$rightSideOpen ? s.$endPanelWidth + s.$countertopSideOverhang : s.$room.$cabinetSideGap
            };
        },
        // End panel itself shouldn't have another end panel added to it.
        $leftSideOpen: (s)=>!$EndPanel.is(s) && s.$freeSpaceLeft > s.$room.$minGapForOpenCabinet,
        $rightSideOpen: (s)=>!$EndPanel.is(s) && s.$freeSpaceRight > s.$room.$minGapForOpenCabinet,
        $netHeight: (s)=>s.$height - s.$room.$toeKickHeight,
        $frontPanelHeight: (s)=>s.$netHeight + s.$frontPanelBottomOverhang,
        $dimensionIndicatorLabelWallFontSize: (s)=>s.$wall.$dimensionIndicatorLabelFontSize,
        // Ignore small parts, which would have unreadable labels.
        $dimensionIndicatorWidthActive: (s)=>s.$netWidth > 5 * 25.4,
        $dimensionIndicatorWidthEndX: (s)=>s.$netWidth
    },
    BottomWallItemWithDefaultToeKick: {
        $toeKickWidth: (s)=>s.$width - (s.$leftSideOpen ? s.$toeKickInset : 0) - (s.$rightSideOpen ? s.$toeKickInset : 0),
        $toeKickOffsetX: (s)=>s.$leftSideOpen ? s.$toeKickInset : 0,
        $toeKickOffsetZ: (s)=>-s.$toeKickInset + s.$room.$doorThickness,
        $toeKickDepth: (s)=>s.$depth - 2 * s.$toeKickInset + s.$room.$doorThickness,
        $toeKickObjectName: (s)=>{
            if (s.$width >= CabinetWidth.W60) {
                return ToeKickObjectName.TOE_KICK_VENT_60IN;
            } else if (s.$width >= CabinetWidth.W48) {
                return ToeKickObjectName.TOE_KICK_VENT_48IN;
            } else if (s.$width >= CabinetWidth.W42) {
                return ToeKickObjectName.TOE_KICK_VENT_42IN;
            } else if (s.$width >= CabinetWidth.W36) {
                return ToeKickObjectName.TOE_KICK_VENT_36IN;
            } else if (s.$width >= CabinetWidth.W30) {
                return ToeKickObjectName.TOE_KICK_VENT_30IN;
            } else if (s.$width >= CabinetWidth.W24) {
                return ToeKickObjectName.TOE_KICK_VENT_24IN;
            } else if (s.$width >= CabinetWidth.W18) {
                return ToeKickObjectName.TOE_KICK_VENT_18IN;
            } else {
                return ToeKickObjectName.TOE_KICK;
            }
        },
        $toeKickWidthScaleZero: (s)=>getToeKickData(s.$toeKickObjectName).scaleZero,
        $toeKickWidthScaleOne: (s)=>getToeKickData(s.$toeKickObjectName).scaleOne
    },
    BottomWallItemWithBackPanel: {
        $backPanelWidth: (s)=>s.$width + 2 * s.$backPanelOverhang,
        // TODO: This might be off - an extra offset is needed
        // to aligh it perfectly with the side panel.
        $backPanelOffsetZ: (s)=>-s.$depth + 0.3 * 25.4
    },
    BottomCabinetWithAppliance: {
        // Appliance is optional.
        $applianceName: (s)=>s.$appliance?.$name ?? s.$name,
        $applianceSku: (s)=>s.$appliance?.$sku ?? s.$sku,
        $applianceDescription: (s)=>s.$appliance?.$description ?? s.$description,
        // Sink base technically doesn't have an appliance, but it has a big hole in it.
        $hasWorkSurface: (s)=>!SinkBase.is(s) && !s.$appliance
    },
    BottomCabinetWithoutAppliance: {
        $hasWorkSurface: ()=>true
    },
    BottomWallItemWithDefaultCountertop: {
        $countertopOffsetX: (s)=>s.$countertopWidth / 2,
        $countertopWidth: (s)=>{
            return s.$width + s.$countertopExtraWidth.left + s.$countertopExtraWidth.right;
        },
        $countertopWidthScaleZero: ()=>defaultCountertopData.scale.width.zero,
        $countertopWidthScaleOne: ()=>defaultCountertopData.scale.width.one
    },
    BottomWallItemWithCustomCountertop: {
        $countertopFilter: (s)=>{
            const objectName = s.$countertopObjectName;
            if (!objectName) {
                return false;
            }
            return {
                navigationStates: [
                    'Room',
                    'RoomContent',
                    'Recipes'
                ]
            };
        },
        $countertopObjectName: (s)=>{
            return match([
                s.type,
                s.$appliance?.type
            ]).with([
                FullDoorBase.type,
                P.nullish
            ], ()=>'COUNTERTOP').with([
                P.any,
                LynxProfessionalSingleBurner.type
            ], ()=>'COUNTERTOP_BURNER_SINGLE').with([
                P.any,
                LynxProfessionalDoubleBurner.type
            ], ()=>'COUNTERTOP_BURNER_DOUBLE').with([
                P.any,
                LynxSedonaSingleBurner.type
            ], ()=>'COUNTERTOP_BURNER_LSB501').with([
                P.any,
                LynxSedonaDoubleBurner.type
            ], ()=>'COUNTERTOP_BURNER_LSB502').with([
                P.any,
                LynxProfessionalPowerBurner.type
            ], ()=>'COUNTERTOP_BURNER_POWER').with([
                P.any,
                EvoAffinity25G.type
            ], ()=>'COUNTERTOP_EVO_AFFINITY_25').with([
                P.any,
                EvoAffinity30G.type
            ], ()=>'COUNTERTOP_EVO_AFFINITY_30').with([
                P.any,
                LynxAsadoGriddgle.type
            ], ()=>'COUNTERTOP_GRIDDLE').with([
                P.any,
                LynxProfessionalGrillW30.type
            ], [
                P.any,
                LynxSedonaGrillW30.type
            ], ()=>'COUNTERTOP_GRILL_30').with([
                P.any,
                LynxProfessionalGrillW36.type
            ], [
                P.any,
                LynxSedonaGrillW36.type
            ], ()=>'COUNTERTOP_GRILL_36').with([
                P.any,
                LynxProfessionalGrillW42.type
            ], [
                P.any,
                LynxSedonaGrillW42.type
            ], ()=>'COUNTERTOP_GRILL_42').with([
                P.any,
                LynxProfessionalGrillW54.type
            ], ()=>'COUNTERTOP_GRILL_54').with([
                P.any,
                LynxKamadoGrill.type
            ], ()=>'COUNTERTOP_SINK').with([
                P.any,
                LynxSinkW18.type
            ], [
                P.any,
                LynxSinkW24.type
            ], [
                P.any,
                LynxSinkW30.type
            ], ()=>'COUNTERTOP_SINK').with([
                P.any,
                LynxProfessionalCocktailStation.type
            ], ()=>'COUNTERTOP_SINK').otherwise(()=>'');
        },
        $countertopOffsetX: (s)=>s.$width / 2,
        $countertopDepthScaleZero: (s)=>getCountertopData(s.$countertopObjectName).scale.depth.zero,
        $countertopDepthScaleOne: (s)=>getCountertopData(s.$countertopObjectName).scale.depth.one,
        // Used for default flat countertop without holes.
        $countertopWidth: (s)=>s.$width + s.$countertopExtraWidth.left + s.$countertopExtraWidth.right,
        $countertopWidthScaleZero: (s)=>getCountertopData(s.$countertopObjectName).scale.width.zero,
        $countertopWidthScaleOne: (s)=>getCountertopData(s.$countertopObjectName).scale.width.one
    },
    TopWallItem: {
        $offsetX: (s)=>s.$netWidth / 2,
        $offsetY: (s)=>Math.max(s.$wall.$maxWallItemHeight, s.$room.$minCabinetHeight) + 30 * 25.4,
        $selectHotspotOffsetX: (s)=>s.$width / 2,
        $selectHotspotOffsetY: (s)=>s.$height / 2,
        $selectHotspotOffsetZ: (s)=>s.$wall.$maxWallItemDepth
    },
    CountertopWallItem: {
        $offsetX: (s)=>s.$netWidth / 2,
        // Models are already positioned on top of the countertop
        $offsetY: ()=>0,
        $offsetZ: (s)=>s.$wall.$maxWallItemDepth,
        $selectHotspotOffsetX: (s)=>s.$width / 2,
        $selectHotspotOffsetY: (s)=>s.$height / 2 + s.$wall.$maxWallItemHeight,
        $selectHotspotOffsetZ: ()=>0
    },
    WallItem: {
        $draggableFilter: (s)=>({
                selectedEntityIds: [
                    s.id
                ]
            }),
        $surfaces: (s)=>{
            const surfaces = match(s.$room.shape).with('I', ()=>{
                return [
                    s.$room.$wallB.$draggingSurface
                ];
            }).with('L', ()=>{
                return [
                    s.$room.$wallA.$draggingSurface,
                    s.$room.$wallB.$draggingSurface
                ];
            }).with('U', ()=>{
                return [
                    s.$room.$wallA.$draggingSurface,
                    s.$room.$wallB.$draggingSurface,
                    s.$room.$wallC.$draggingSurface
                ];
            }).exhaustive();
            return surfaces;
        },
        ...StackExpressions
    },
    Drawer33Door66: {
        $doorHeight: (s)=>s.$frontPanelHeight - (s.$drawerOffsetY - s.$doorOffsetY),
        $doorOffsetY: (s)=>s.$height - s.$drawerHeight - 0.005 * s.$frontPanelHeight,
        $drawerOffsetX: ()=>0,
        $drawerOffsetY: (s)=>s.$height,
        $drawerHeight: (s)=>s.$frontPanelHeight * 0.33,
        $drawerHandleOffsetX: (s)=>s.$netWidth / 2,
        $drawerHandleOffsetY: (s)=>s.$height - s.$handleSideOffset
    },
    EvoBaseW36: {
        $doorHeight: (s)=>s.$frontPanelHeight * 0.664,
        $doorOffsetY: (s)=>s.$frontPanelHeight * 0.664 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $countertopLeftSideOuterWidth: (s)=>s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: ()=>0
    },
    FullDoorBase: {
        $doorHeight: (s)=>s.$frontPanelHeight,
        $doorOffsetY: (s)=>s.$frontPanelHeight + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $countertopSideOuterWidth: ()=>0,
        $countertopSideInnerWidth: ()=>0,
        $countertopLeftSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.right + s.$room.$cabinetSideGap
    },
    GrillBase: {
        $dataKey: (s)=>`${s.type}_${round(s.$netWidth, 2)}_${s.variant}`,
        $countertopLeftSideOuterWidth: (s)=>s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: ()=>0,
        $grillBodyObjectName: (s)=>{
            return match(s.variant).with(GrillBaseVariant.WithWarmingDrawer, ()=>'CABINET_LYNX_GBFD_WD_BODY').otherwise(()=>'CABINET_LYNX_GBFD_BODY');
        },
        $doorHeight: (s)=>s.$frontPanelHeight * 0.664,
        $doorOffsetY: (s)=>s.$frontPanelHeight * 0.664 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $warmingDrawerPanelColor: (s)=>getAppliancePanelMaterialProperties(s.$warmingDrawer?.powderCoating ?? false, s.$room.cabinetColor).color,
        $warmingDrawerPanelMetalness: (s)=>getAppliancePanelMaterialProperties(s.$warmingDrawer?.powderCoating ?? false, s.$room.cabinetColor).metalness,
        $warmingDrawerPanelRoughness: (s)=>getAppliancePanelMaterialProperties(s.$warmingDrawer?.powderCoating ?? false, s.$room.cabinetColor).roughness,
        $warmingDrawerPanelRoughnessMapUrl: (s)=>getAppliancePanelMaterialProperties(s.$warmingDrawer?.powderCoating ?? false, s.$room.cabinetColor).roughnessMapUrl,
        $warmingDrawerPanelNormalMapUrl: (s)=>getAppliancePanelMaterialProperties(s.$warmingDrawer?.powderCoating ?? false, s.$room.cabinetColor).normalMapUrl
    },
    KamadoGrillBase: {
        $doorHeight: (s)=>s.$frontPanelHeight * 0.375,
        $doorOffsetY: (s)=>s.$frontPanelHeight * 0.375 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $countertopSideOuterWidth: ()=>-CabinetWidth.W3,
        $countertopLeftSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: ()=>CabinetWidth.W2
    },
    ResizableWallItem: {
        $netWidth: (s)=>{
            return s.width;
        }
    },
    LynxSinkBase: {
        $doorHeight: (s)=>s.$frontPanelHeight * 0.664,
        $doorOffsetY: (s)=>s.$frontPanelHeight * 0.664 + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $countertopSideOuterWidth: (s)=>{
            const $appliance = s.$appliance;
            if (!$appliance || !LynxSink.is($appliance)) {
                return 0;
            }
            return match([
                s.width,
                $appliance?.width
            ]).with([
                CabinetWidth.W24,
                CabinetWidth.W18
            ], ()=>-CabinetWidth.W6).with([
                CabinetWidth.W30,
                CabinetWidth.W24
            ], ()=>-CabinetWidth.W3).otherwise(()=>CabinetWidth.W0);
        },
        $countertopLeftSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: (s)=>{
            const $appliance = s.$appliance;
            if (!$appliance || !LynxSink.is($appliance)) {
                return CabinetWidth.W0;
            }
            return match([
                s.width,
                $appliance?.width
            ]).with([
                CabinetWidth.W24,
                CabinetWidth.W18
            ], ()=>CabinetWidth.W6).with([
                CabinetWidth.W30,
                CabinetWidth.W24
            ], ()=>CabinetWidth.W3).otherwise(()=>CabinetWidth.W0);
        }
    },
    SinkBase: {
        $doorHeight: (s)=>s.$frontPanelHeight,
        $doorOffsetY: (s)=>s.$frontPanelHeight + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang,
        $countertopSideOuterWidth: (s)=>{
            const $appliance = s.$appliance;
            if (!$appliance || !LynxSink.is($appliance)) {
                return 0;
            }
            return match([
                s.width,
                $appliance?.width
            ]).with([
                CabinetWidth.W24,
                CabinetWidth.W18
            ], ()=>-CabinetWidth.W6).with([
                CabinetWidth.W30,
                CabinetWidth.W24
            ], ()=>-CabinetWidth.W3).otherwise(()=>CabinetWidth.W0);
        },
        $countertopLeftSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.left + s.$room.$cabinetSideGap,
        $countertopRightSideOuterWidth: (s)=>s.$countertopSideOuterWidth + s.$countertopExtraWidth.right + s.$room.$cabinetSideGap,
        $countertopSideInnerWidth: (s)=>{
            const $appliance = s.$appliance;
            if (!$appliance || !LynxSink.is($appliance)) {
                return CabinetWidth.W0;
            }
            return match([
                s.width,
                $appliance?.width
            ]).with([
                CabinetWidth.W24,
                CabinetWidth.W18
            ], ()=>CabinetWidth.W6).with([
                CabinetWidth.W30,
                CabinetWidth.W24
            ], ()=>CabinetWidth.W3).otherwise(()=>CabinetWidth.W0);
        }
    },
    ThreeDrawerBase: {
        $drawerHeight: (s)=>s.$frontPanelHeight * 0.33,
        $drawerOffsetX: ()=>0,
        $handleOffsetZ: (s)=>s.$handleDepthOffset,
        $handleLength: (s)=>s.$room.$smallHandleLength,
        $drawerHandleOffsetX: (s)=>s.$netWidth / 2,
        $drawerOneOffsetY: (s)=>s.$height,
        $drawerOneHandleOffsetY: (s)=>s.$drawerOneOffsetY - s.$handleSideOffset,
        $drawerTwoOffsetY: (s)=>s.$height - (s.$drawerHeight + 0.005 * s.$frontPanelHeight),
        $drawerTwoHandleOffsetY: (s)=>s.$drawerTwoOffsetY - s.$handleSideOffset,
        $drawerThreeOffsetY: (s)=>s.$height - 2 * (s.$drawerHeight + 0.005 * s.$frontPanelHeight),
        $drawerThreeHandleOffsetY: (s)=>s.$drawerThreeOffsetY - s.$handleSideOffset
    },
    TrashPulloutBase: {
        $doorHeight: (s)=>s.$frontPanelHeight,
        $doorOffsetY: (s)=>s.$frontPanelHeight + s.$room.$toeKickHeight - s.$frontPanelBottomOverhang
    }
});

//# sourceMappingURL=cabinets.js.map